import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import RecentProjects from '../components/dashboard/RecentProjects';
import ApplicationsSection from '../components/dashboard/sections/ApplicationsSection';
import MOMSection from '../components/dashboard/sections/MOMSection';
import ProposalsSection from '../components/dashboard/sections/ProposalsSection';
import PaymentsSection from '../components/dashboard/sections/PaymentsSection';
import DocumentsSection from '../components/dashboard/sections/DocumentsSection';
import { Card, Spinner } from '../components/ui';
import { api } from '../services/api/client';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const DashboardClient: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [myProjects, setMyProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Single source of truth: per-user can_access_* flags decide module access.
  const canDocuments = user?.can_access_documents;
  const canProposals = user?.can_access_proposals;
  const canPayments = user?.can_access_payments;

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const response = await api.get('/dashboard/client/');
      setDashboardData(response.data.data || response.data);


      if (user?.can_access_projects) {
        try {
          const projectsResponse = await api.get('/projects/');
          const projectsData = projectsResponse.data.data?.results || projectsResponse.data.results || [];
          setMyProjects(projectsData.slice(0, 5));
        } catch {
          setMyProjects([]);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(null);
      setMyProjects([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid">
        {/* Page header */}
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-1">{t('myDashboard')}</h2>
          <p className="text-sm text-gray-500 mb-0">{t('trackApplicationsStatus')}</p>
        </div>

        {loading ? (
          <Card>
            <div className="flex justify-center py-10">
              <Spinner size={40} />
            </div>
          </Card>
        ) : (
          <>
            {/* Applications overview + embedded recents */}
            <ApplicationsSection role="client" data={dashboardData} />

            {/* Projects: the statistics endpoint is staff-only, so clients
                get their recent-projects list instead of aggregate charts. */}
            {user?.can_access_projects && myProjects.length > 0 && (
              <div className="mb-6">
                <RecentProjects projects={myProjects} showAllLink />
              </div>
            )}

            {/* Module sections where this client's representative has access —
                each endpoint scopes to the client and sections hide on 403. */}
            {user?.can_access_mom && <MOMSection />}
            {canProposals && <ProposalsSection />}
            {canPayments && <PaymentsSection />}
            {canDocuments && <DocumentsSection />}
          </>
        )}
      </div>
    </Layout>
  );
};

export default DashboardClient;
